import Form from "@/components/Form/Form";
import { Heading } from "@medoma/components/Heading/Heading";
import { TemplateActivityCategoryPicker } from "@/pages/template-manager/TemplateForm/TemplateFormFields/TemplateActivityCategoryPicker";
import { getActivityCategoryConfig } from "@medoma/models/activities";
import { FormProvider, useForm } from "react-hook-form";
import { TemplateTitleInput } from "./TemplateFormFields/TemplateTitleInput";
import { TemplateDescriptionInput } from "./TemplateFormFields/TemplateDescriptionInput";
import { TemplateScheduling } from "./TemplateFormFields/TemplateScheduling/TemplateScheduling";
import Checkboxes from "@/components/Checkbox/Checkboxes";
import Checkbox from "@/components/Checkbox/Checkbox";
import { Trans, useLingui } from "@lingui/react/macro";
import { categorySchema } from "@medoma/models/activities";
import { TemplateMeasurementsPicker } from "./TemplateFormFields/TemplateMeasurementsPicker";
import { knownFeatureFlagsSchema, useFeatureFlag } from "@/api/FeatureFlags";
import { TemplateDuration } from "./TemplateFormFields/TemplateDuration/TemplateDuration";
import { TemplateCompetencePicker } from "./TemplateFormFields/TemplateCompetencePicker";
import { TemplateAddInvoicingCodesAction } from "./TemplateFormFields/TemplateAddInvoicingCodesAction";
import { FilledButton, PlainButton } from "@medoma/components/Button/Button";
import { useEffect } from "react";
import { TemplatePatientType } from "./TemplateFormFields/TemplatePatientType";
import { organizationUsesInvoicingCodes } from "@medoma/models/organizations";
import { useUserProfile } from "@/api/Users";
import type { TemplateFormFields } from "./templateFormSchema";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createActivityTemplate,
  deleteTemplate,
  templatesKeys,
  updateActivityTemplate,
  updateTagsForTemplate,
  activityTemplateQueryOptions,
  type ITemplateTag,
} from "@/api/Templates";
import { useNavigate, useParams } from "react-router";
import { z } from "zod";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import styles from "./TemplateForm.module.scss";
import {
  mapEmptyCheckboxFieldsFromFalseToEmptyArray,
  useParentRoutePatientTypes,
} from "../templateManagerUtils";
import { TemplateFieldValidationContextProvider } from "../TemplateFieldValidationContextProvider";
import BinIcon from "@medoma/components/icons/BinIcon";
import {
  GroupSelect,
  NO_GROUP_ID,
  NO_PATIENT_TYPE_SELECTED,
  type GroupSelectFields,
} from "../GroupForm/GroupSelect";
import {
  getDefaultGroupId,
  getDefaultCompetenceTagIds,
  getTagIdsFromForm,
} from "../tagUtils";
import {
  CompetenceTagsPicker,
  type CompetenceTagsPickerFields,
} from "../CompetenceTags/CompetenceTagsPicker";
import { Loading } from "@medoma/components/Loading/Loading";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { deducedError } from "@/Utils/ErrorUtils";

type TemplateFormProps =
  | {
      mode: "create";
      templateId?: never;
      defaultValues?: never;
      tags?: never;
    }
  | {
      mode: "edit";
      templateId: string;
      defaultValues: TemplateFormFields;
      tags: ITemplateTag[];
    };

const TemplateForm = (props: TemplateFormProps) => {
  const { mode, templateId, defaultValues, tags } = props;
  const { t } = useLingui();

  const { data: showDurationInUI } = useFeatureFlag(
    knownFeatureFlagsSchema.enum.ShowDurationInUI,
  );
  const { organization } = useUserProfile();

  const patientTypes = useParentRoutePatientTypes();

  const methods = useForm<
    TemplateFormFields & GroupSelectFields & CompetenceTagsPickerFields
  >({
    defaultValues: defaultValues
      ? {
          ...defaultValues,
          groupId: getDefaultGroupId(tags ?? []),
          competenceTagsIds: getDefaultCompetenceTagIds(tags ?? []),
        }
      : {
          patientType: patientTypes.length === 1 ? patientTypes[0] : undefined,
          groupId:
            patientTypes.length === 1 ? NO_GROUP_ID : NO_PATIENT_TYPE_SELECTED,
          competenceTagsIds: [],
        },
  });

  const {
    handleSubmit,
    formState: { errors },
    register,
    watch,
    setValue,
    reset,
    unregister,
  } = methods;

  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { mutate: createTemplateMutation, isPending: isPendingCreate } =
    useMutation({
      mutationFn: (
        data: TemplateFormFields &
          GroupSelectFields &
          CompetenceTagsPickerFields,
      ) => {
        const { groupId, competenceTagsIds, ...formData } = data;
        return createActivityTemplate({
          formData,
          tagIds: getTagIdsFromForm(data),
        });
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: templatesKeys.all });
        reset();
      },
    });

  const { mutate: updateTemplateMutation, isPending: isPendingUpdate } =
    useMutation({
      mutationFn: async ({
        id,
        data,
      }: {
        id: string;
        data: TemplateFormFields &
          GroupSelectFields &
          CompetenceTagsPickerFields;
      }) => {
        const { groupId, competenceTagsIds, ...formData } = data;
        await updateActivityTemplate(id, formData);
        await updateTagsForTemplate({ id, tagIds: getTagIdsFromForm(data) });
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: templatesKeys.all });
      },
    });

  const { mutate: deleteTemplateMutation, isPending: isPendingDelete } =
    useMutation({
      mutationFn: (id: string) => deleteTemplate(id),
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: templatesKeys.all });
        void navigate("..");
      },
    });

  const validateAndSubmit = handleSubmit((data) => {
    const mappedData = {
      ...mapEmptyCheckboxFieldsFromFalseToEmptyArray(data),
    };

    if (mode === "create") {
      createTemplateMutation(mappedData);
    } else {
      updateTemplateMutation({ id: templateId, data: mappedData });
    }
  });

  // Acceptable. We don't use react compiler, we just lint to find incompatibilities, such as this.
  // oxlint-disable-next-line react/incompatible-library
  const currentCategory = watch("category");
  const selectedPatientType = watch("patientType");
  const currentGroupId = watch("groupId");
  const hasPatient = watch("withoutPatient") !== true;

  const {
    allowsHidden,
    allowsCompetence,
    requiresCompetence,
    allowsInvoicingCode: selectedCategoryAllowsInvoicingCode,
    allowsDuration,
    allowsDoubleStaffing,
  } = currentCategory
    ? getActivityCategoryConfig({
        category: currentCategory,
        activityHasPatient: hasPatient,
      })
    : {
        allowsHidden: false,
        allowsCompetence: false,
        allowsInvoicingCode: false,
        allowsDuration: false,
        allowsDoubleStaffing: false,
      };

  const clearCategoryDependentFields = () => {
    setValue("procedureCodes", []);
    setValue("productCodes", []);
    setValue("doubleStaffing", false);
    setValue("measurements", []);
    setValue("hidden", false);
    setValue("requiredCompetences", []);
  };

  // When a patient type is selected, ensure groupId is never NO_PATIENT_TYPE_SELECTED
  // Reset to NO_GROUP_ID if it's still the placeholder value
  useEffect(() => {
    if (selectedPatientType && currentGroupId === NO_PATIENT_TYPE_SELECTED) {
      setValue("groupId", NO_GROUP_ID);
    }
  }, [selectedPatientType, currentGroupId, setValue]);

  // When "without patient" is selected, patient type is not to be set
  // When "without patient" is selected, group can not be selected (for simplicity reasons)
  // but we leave this field in the form, and let NO_GROUP_ID be set for all tag logic to work as intended.
  // For now, we hide the group input - this can be revealed again if grouping is implemented for
  // templates without patient.
  useEffect(() => {
    if (!hasPatient) {
      unregister("patientType");
    }
  }, [hasPatient, unregister]);

  const isPendingMutation =
    isPendingCreate || isPendingUpdate || isPendingDelete;

  return (
    <FormProvider {...methods}>
      <TemplateFieldValidationContextProvider
        value={{ isInFinalValidationPhase: false }}
      >
        <Form onSubmit={validateAndSubmit}>
          <TemplateTitleInput />

          <Checkbox
            label={{ text: t`Utan patient` }}
            {...register("withoutPatient")}
          />

          {/* If there are more than one patient type, render TemplatePatientType */}
          {hasPatient && patientTypes.length > 1 ? (
            <TemplatePatientType availablePatientTypes={patientTypes} />
          ) : null}

          {/* Not possible to select group if without patient (for simplicity reasons). OK to add this feature if needed. */}
          {hasPatient ? (
            <GroupSelect selectedPatientType={selectedPatientType} />
          ) : null}

          <CompetenceTagsPicker />

          <TemplateActivityCategoryPicker
            onCategoryChange={clearCategoryDependentFields}
            templateIsWithoutPatient={!hasPatient}
          />
          {currentCategory === categorySchema.enum.PatientMeasurementTask ? (
            <TemplateMeasurementsPicker />
          ) : null}
          <TemplateDescriptionInput />
          <TemplateScheduling />

          {showDurationInUI && allowsDuration ? <TemplateDuration /> : null}
          {allowsCompetence ? (
            <TemplateCompetencePicker
              isRequiredBySelectedCategory={requiresCompetence}
            />
          ) : null}

          {/* OTHER */}
          {allowsDoubleStaffing || allowsHidden ? (
            <Checkboxes legend={t`Övrigt`} orientation="horizontal">
              {allowsDoubleStaffing ? (
                <Checkbox
                  errorMessage={errors.doubleStaffing?.message}
                  label={{ text: t`Kräver dubbelbemanning` }}
                  visualStyle="framed"
                  {...register("doubleStaffing")}
                />
              ) : (
                <></>
              )}
              {allowsHidden ? (
                <Checkbox
                  errorMessage={errors.hidden?.message}
                  label={{ text: t`Dölj aktiviteten för patienten` }}
                  visualStyle="framed"
                  {...register("hidden")}
                />
              ) : (
                <></>
              )}
            </Checkboxes>
          ) : null}

          {selectedCategoryAllowsInvoicingCode &&
          selectedPatientType &&
          organizationUsesInvoicingCodes(organization) ? (
            <TemplateAddInvoicingCodesAction
              selectedPatientType={selectedPatientType}
            />
          ) : null}

          <div className={styles.actions}>
            <FilledButton type="submit" disabled={isPendingMutation}>
              <Trans>Spara</Trans>
            </FilledButton>
            {mode === "edit" ? (
              <PlainButton
                type="button"
                disabled={isPendingMutation}
                onClick={() => deleteTemplateMutation(templateId)}
              >
                <BinIcon />
                <Trans>Ta bort</Trans>
              </PlainButton>
            ) : null}
          </div>
        </Form>
      </TemplateFieldValidationContextProvider>
    </FormProvider>
  );
};

export default TemplateForm;

export const NewTemplateForm = () => {
  return (
    <>
      <header className={styles.header}>
        <Heading level="h1">
          <Trans>Ny aktivitet</Trans>
        </Heading>
      </header>
      <TemplateForm key="new" mode="create" />
    </>
  );
};

export const EditTemplateForm = () => {
  const { t } = useLingui();
  const { id } = z.object({ id: z.string() }).parse(useParams());

  const {
    data: template,
    isPending,
    isError,
    error,
  } = useQuery(activityTemplateQueryOptions(id));

  if (isPending) {
    return <Loading message={t`Hämtar mall`} />;
  }

  if (isError) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta mall.`} ${deducedError(error)}`}
      />
    );
  }

  return (
    <>
      <header className={styles.header}>
        <CategoryIcon category={template.template.category} />
        <Heading level="h1">{template.template.title}</Heading>
      </header>
      <TemplateForm
        key={id}
        templateId={id}
        defaultValues={template.template}
        tags={template.tags}
        mode="edit"
      />
    </>
  );
};
